import { Navigate, createBrowserRouter, type RouteObject } from 'react-router-dom';

import { AuthenticatedLayout } from './authenticated-layout.js';
import { RouteLoadError, RouteLoading } from './route-loading.js';
import { appRouteHandles } from './route-navigation.js';
import { SessionBoundary } from './session.js';

export const appRoutes: RouteObject[] = [
  {
    path: '/login',
    lazy: async () => {
      const module = await import('../features/auth/login-page.js');
      return { Component: module.LoginPage };
    },
    hydrateFallbackElement: <RouteLoading />,
    errorElement: <RouteLoadError />,
    handle: appRouteHandles.login,
  },
  {
    element: (
      <SessionBoundary>
        <AuthenticatedLayout />
      </SessionBoundary>
    ),
    hydrateFallbackElement: <RouteLoading />,
    children: [
      lazyPage('/dashboard', appRouteHandles.dashboard, async () => {
        const module = await import('../features/dashboard/dashboard-page.js');
        return { Component: module.DashboardPage };
      }),
      lazyPage('/employees', appRouteHandles.employeeList, async () => {
        const module = await import('../features/employees/employee-list-page.js');
        return { Component: module.EmployeeListPage };
      }),
      {
        path: '/employees/:id',
        lazy: async () => {
          const module = await import('../features/employees/employee-detail-page.js');
          return { Component: module.EmployeeDetailPage };
        },
        hydrateFallbackElement: <RouteLoading />,
        errorElement: <RouteLoadError safeReturnTo="/employees" />,
        handle: appRouteHandles.employeeDetail,
      },
      {
        path: '/purchases/activity',
        lazy: async () => {
          const module = await import('../features/purchases/purchase-activity-page.js');
          return { Component: module.PurchaseActivityPage };
        },
        hydrateFallbackElement: <RouteLoading />,
        errorElement: <RouteLoadError />,
        handle: appRouteHandles.purchaseActivity,
      },
      lazyPage('/trials', appRouteHandles.trialList, async () => {
        const module = await import('../features/trials/trial-list-page.js');
        return { Component: module.TrialListPage };
      }),
      lazyPage('/trials/appointments/:id/assessment', appRouteHandles.trialAssessment, async () => {
        const module = await import('../features/teaching/trial-assessment-page.js');
        return { Component: module.TrialAssessmentPage };
      }),
      {
        path: '/trials/appointments/:id',
        lazy: async () => {
          const module = await import('../features/trials/trial-detail-page.js');
          return { Component: module.TrialDetailPage };
        },
        hydrateFallbackElement: <RouteLoading />,
        errorElement: <RouteLoadError safeReturnTo="/trials" />,
        handle: appRouteHandles.trialDetail,
      },
      lazyPage('/students', appRouteHandles.studentList, async () => {
        const module = await import('../features/students/student-list-page.js');
        return { Component: module.StudentListPage };
      }),
      {
        path: '/students/:id',
        lazy: async () => {
          const module = await import('../features/students/student-detail-page.js');
          return { Component: module.StudentDetailPage };
        },
        hydrateFallbackElement: <RouteLoading />,
        errorElement: <RouteLoadError safeReturnTo="/students" />,
        handle: appRouteHandles.studentDetail,
      },
      {
        path: '/package-templates',
        lazy: async () => {
          const module = await import('../features/catalogs/package-template-list-page.js');
          return { Component: module.PackageTemplateListPage };
        },
        hydrateFallbackElement: <RouteLoading />,
        errorElement: <RouteLoadError />,
        handle: appRouteHandles.packageTemplateList,
      },
      {
        path: '/package-templates/:id',
        lazy: async () => {
          const module = await import('../features/catalogs/package-template-detail-page.js');
          return { Component: module.PackageTemplateDetailPage };
        },
        hydrateFallbackElement: <RouteLoading />,
        errorElement: <RouteLoadError safeReturnTo="/package-templates" />,
        handle: appRouteHandles.packageTemplateDetail,
      },
      {
        path: '/commission-formulas',
        element: <Navigate replace to="/commission-plans?tab=formulas" />,
        handle: appRouteHandles.formulaList,
      },
      {
        path: '/commission-plans',
        lazy: async () => {
          const module = await import('../features/commission-plans/commission-plan-page.js');
          return { Component: module.CommissionPlanPage };
        },
        hydrateFallbackElement: <RouteLoading />,
        errorElement: <RouteLoadError />,
        handle: appRouteHandles.commissionPlanList,
      },
      {
        path: '/consumptions',
        lazy: async () => {
          const module = await import('../features/consumptions/consumption-list-page.js');
          return { Component: module.ConsumptionListPage };
        },
        hydrateFallbackElement: <RouteLoading />,
        errorElement: <RouteLoadError />,
        handle: appRouteHandles.consumptionList,
      },
      {
        path: '/analytics/consumption',
        lazy: async () => {
          const module = await import('../features/analytics/analytics-center-page.js');
          return { Component: module.AnalyticsLegacyRedirect };
        },
        hydrateFallbackElement: <RouteLoading />,
        errorElement: <RouteLoadError />,
        handle: appRouteHandles.analyticsConsumption,
      },
      {
        path: '/analytics',
        lazy: async () => {
          const module = await import('../features/analytics/analytics-center-page.js');
          return { Component: module.AnalyticsCenterPage };
        },
        hydrateFallbackElement: <RouteLoading />,
        errorElement: <RouteLoadError />,
        handle: appRouteHandles.analyticsConsumption,
      },
      {
        path: '/consumption-batches/new',
        lazy: async () => {
          const module = await import('../features/consumptions/consumption-create-page.js');
          return { Component: module.ConsumptionCreatePage };
        },
        hydrateFallbackElement: <RouteLoading />,
        errorElement: <RouteLoadError safeReturnTo="/consumptions" />,
        handle: appRouteHandles.consumptionCreate,
      },
      {
        path: '/consumption-batches/:id',
        lazy: async () => {
          const module = await import('../features/consumptions/consumption-detail-page.js');
          return { Component: module.ConsumptionDetailPage };
        },
        hydrateFallbackElement: <RouteLoading />,
        errorElement: <RouteLoadError safeReturnTo="/consumptions" />,
        handle: appRouteHandles.consumptionDetail,
      },
      {
        path: '/commissions',
        lazy: async () => {
          const module = await import('../features/commissions/commission-summary-page.js');
          return { Component: module.CommissionSummaryPage };
        },
        hydrateFallbackElement: <RouteLoading />,
        errorElement: <RouteLoadError />,
        handle: appRouteHandles.commissionList,
      },
      {
        path: '/sales-commissions',
        lazy: async () => {
          const module = await import('../features/sales-commissions/sales-commission-page.js');
          return { Component: module.SalesCommissionPage };
        },
        hydrateFallbackElement: <RouteLoading />,
        errorElement: <RouteLoadError />,
        handle: appRouteHandles.salesCommissionList,
      },
      {
        path: '/commissions/:coachId',
        lazy: async () => {
          const module = await import('../features/commissions/commission-detail-page.js');
          return { Component: module.CommissionDetailPage };
        },
        hydrateFallbackElement: <RouteLoading />,
        errorElement: <RouteLoadError safeReturnTo="/commissions" />,
        handle: appRouteHandles.commissionDetail,
      },
      lazyPage('/exports', appRouteHandles.exportList, async () => {
        const module = await import('../features/exports/export-task-page.js');
        return { Component: module.ExportTaskPage };
      }),
      lazyPage('/audits', appRouteHandles.auditList, async () => {
        const module = await import('../features/audits/audit-page.js');
        return { Component: module.AuditPage };
      }),
      {
        path: '/daily-task-templates',
        lazy: async () => {
          const module = await import('../features/daily-tasks/daily-task-template-page.js');
          return { Component: module.DailyTaskTemplatePage };
        },
        hydrateFallbackElement: <RouteLoading />,
        errorElement: <RouteLoadError />,
        handle: appRouteHandles.dailyTaskTemplateList,
      },
      {
        path: '/daily-task-reviews',
        lazy: async () => {
          const module = await import('../features/daily-tasks/daily-task-review-page.js');
          return { Component: module.DailyTaskReviewPage };
        },
        hydrateFallbackElement: <RouteLoading />,
        errorElement: <RouteLoadError />,
        handle: appRouteHandles.dailyTaskReviewList,
      },
      {
        path: '/daily-task-instances/:id',
        lazy: async () => {
          const module = await import('../features/daily-tasks/daily-task-instance-page.js');
          return { Component: module.DailyTaskInstancePage };
        },
        hydrateFallbackElement: <RouteLoading />,
        errorElement: <RouteLoadError safeReturnTo="/dashboard" />,
        handle: appRouteHandles.dailyTaskInstanceDetail,
      },
      {
        path: '/daily-task-evidence',
        lazy: async () => {
          const module = await import('../features/daily-tasks/daily-task-evidence-page.js');
          return { Component: module.DailyTaskEvidencePage };
        },
        hydrateFallbackElement: <RouteLoading />,
        errorElement: <RouteLoadError />,
        handle: appRouteHandles.dailyTaskEvidenceList,
      },
      {
        path: '/account/password',
        lazy: async () => {
          const module = await import('../features/auth/password-page.js');
          return { Component: module.PasswordPage };
        },
        hydrateFallbackElement: <RouteLoading />,
        errorElement: <RouteLoadError />,
        handle: appRouteHandles.accountPassword,
      },
    ],
  },
  { path: '*', element: <Navigate replace to="/dashboard" /> },
];

export function createAppBrowserRouter() {
  return createBrowserRouter(appRoutes);
}

function lazyPage(
  path: string,
  handle: RouteObject['handle'],
  lazy: NonNullable<RouteObject['lazy']>,
): RouteObject {
  return {
    path,
    lazy,
    hydrateFallbackElement: <RouteLoading />,
    errorElement: <RouteLoadError />,
    handle,
  };
}
